// imports
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Paper, Portal, Tooltip, useMediaQuery } from '@mui/material';
import { Close } from '@mui/icons-material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import { useDispatch, useSelector } from 'react-redux';

// local imports
import CustomButton from '../../CustomButton';
import AwdLegend from '../../AwdLegend/AwdLegend';
import { sharedTooltipProps } from '../CustomMapControls/sharedTooltipProps';
import { RootState } from '../../../store';
import { setSidePanelOpen } from '../../../store/mapSlice';

function LegendModal({ onClose, onTabOut, onShiftTabOut, anchorEl }: { onClose: () => void; onTabOut: () => void; onShiftTabOut: () => void; anchorEl: HTMLElement | null }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ x: number; y: number; top: number; left: number } | null>(null);
  const hasBeenDragged = useRef(false);
  const dragCleanupRef = useRef<(() => void) | null>(null);

  const updatePosition = useCallback(() => {
    const el = panelRef.current;
    if (!el || dragStartRef.current || hasBeenDragged.current) return;
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      el.style.top = `${rect.top}px`;
      el.style.right = `${window.innerWidth - rect.left + 10}px`;
      el.style.left = 'auto';
    }
  }, [anchorEl]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useEffect(() => {
    return () => { dragCleanupRef.current?.(); };
  }, []);

  const handleDragStart = (e: React.PointerEvent) => {
    e.preventDefault();
    const el = panelRef.current;
    if (!el) return;
    hasBeenDragged.current = true;
    // Switch from right-based to left-based positioning for dragging
    const currentLeft = el.getBoundingClientRect().left;
    el.style.left = `${currentLeft}px`;
    el.style.right = 'auto';
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      top: el.offsetTop,
      left: currentLeft,
    };
    const handlePointerMove = (ev: PointerEvent) => {
      if (!dragStartRef.current || !panelRef.current) return;
      panelRef.current.style.top = `${dragStartRef.current.top + (ev.clientY - dragStartRef.current.y)}px`;
      panelRef.current.style.left = `${dragStartRef.current.left + (ev.clientX - dragStartRef.current.x)}px`;
    };
    const handlePointerUp = () => {
      dragStartRef.current = null;
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      dragCleanupRef.current = null;
    };
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    dragCleanupRef.current = () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };
  };

  const handleDragKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      onShiftTabOut();
      return;
    }
    const el = panelRef.current;
    if (!el) return;
    const step = 10;
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      if (!hasBeenDragged.current) {
        hasBeenDragged.current = true;
        const currentLeft = el.getBoundingClientRect().left;
        el.style.left = `${currentLeft}px`;
        el.style.right = 'auto';
      }
    }
    switch (e.key) {
      case 'ArrowUp':
        el.style.top = `${parseInt(el.style.top || '0') - step}px`;
        break;
      case 'ArrowDown':
        el.style.top = `${parseInt(el.style.top || '0') + step}px`;
        break;
      case 'ArrowLeft':
        el.style.left = `${parseInt(el.style.left || '0') - step}px`;
        break;
      case 'ArrowRight':
        el.style.left = `${parseInt(el.style.left || '0') + step}px`;
        break;
    }
  };

  return (
    <Box
      ref={panelRef}
      sx={{
        position: 'absolute',
        zIndex: 5000,
        display: 'flex',
        alignItems: 'flex-start',
        maxWidth: '90vw',
      }}
    >
      <Box
        role="application"
        aria-label="Drag handle for map legend"
        sx={{ marginRight: '-1px', zIndex: 1 }}
      >
        <Box
          role="button"
          tabIndex={0}
          aria-label="Drag or use arrow keys to reposition legend"
          data-legend-drag-handle
          onPointerDown={(e) => { e.stopPropagation(); handleDragStart(e); }}
          onKeyDown={handleDragKeyDown}
          sx={{
            pointerEvents: 'auto',
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 32,
            flexShrink: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderTop: '1px solid #d3d3d3',
            borderLeft: '1px solid #d3d3d3',
            borderBottom: '1px solid #d3d3d3',
            borderRight: 'none',
            borderRadius: '4px 0 0 4px',
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 16, color: '#666' }} />
        </Box>
      </Box>
      <Box
        role="region"
        aria-label="Map legend"
        sx={{
          p: '8px',
          paddingRight: '36px',
          overflowY: 'auto',
          maxHeight: '80vh',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          border: '1px solid #d3d3d3',
          borderTopLeftRadius: 0,
          borderTopRightRadius: '4px',
          borderBottomLeftRadius: '4px',
          borderBottomRightRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          position: 'relative',
        }}
      >
        <AwdLegend />
        <IconButton
          aria-label="Close legend"
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === 'Tab' && !e.shiftKey) {
              e.preventDefault();
              onTabOut();
            }
          }}
          size="small"
          sx={{ position: 'absolute', top: 4, right: 4 }}
        >
          <Close />
        </IconButton>
      </Box>
    </Box>
  );
}

function LegendBottomSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  if (!open) return null;
  return (
    <Paper
      elevation={8}
      role="region"
      aria-label="Map legend"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        maxHeight: '50vh',
        borderRadius: '16px 16px 0 0',
        zIndex: 1400,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: 1,
          flexShrink: 0,
        }}
      />
      <IconButton
        ref={closeRef}
        aria-label="Close legend"
        onClick={onClose}
        size="small"
        sx={{ position: 'absolute', top: 8, right: 8 }}
      >
        <Close />
      </IconButton>
      <Box sx={{ px: 2, pb: 2, overflowY: 'auto' }}>
        <AwdLegend />
      </Box>
    </Paper>
  );
}

/**
 * LegendButton
 *
 * Renders a toggle button that opens the map legend.
 * On desktop, the legend is a draggable panel positioned next to the button via a Portal.
 * On mobile, it renders as a bottom sheet.
 * The legend and side panel are mutually exclusive on mobile.
 *
 * @returns {JSX.Element} The rendered LegendButton component.
 */

const LegendButton = React.memo(function LegendButton() {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const prevAppliedRef = useRef({ model: '', variable: '' });
  const appliedModel = useSelector(
    (state: RootState) => state.waterData.appliedModel
  );
  const appliedVariable = useSelector(
    (state: RootState) => state.waterData.appliedVariable
  );
  const awdNoData = useSelector(
    (state: RootState) => state.ui.awdNoData
  );
  const awdNoDataProbing = useSelector(
    (state: RootState) => state.ui.awdNoDataProbing
  );
  const primaryNavOpen = useSelector(
    (state: RootState) => state.map.primaryNavOpen
  );
  const sidePanelOpen = useSelector(
    (state: RootState) => state.map.sidePanelOpen
  );
  const isSmallScreen = useMediaQuery('(max-width: 1023px)');
  const isMobile = useMediaQuery('(max-width: 650px)');
  const prevIsMobileRef = useRef(isMobile);
  const hideControls = (isSmallScreen && primaryNavOpen) || (isMobile && sidePanelOpen);

  // On resize to mobile: if legend is open, close side panel (legend has seniority)
  useEffect(() => {
    if (isMobile && !prevIsMobileRef.current && isOpen && sidePanelOpen) {
      dispatch(setSidePanelOpen(false));
    }
    prevIsMobileRef.current = isMobile;
  }, [isMobile, isOpen, sidePanelOpen, dispatch]);

  // Open legend on initial load (once probe completes) and reopen when model/variable changes.
  // Never auto-open on mobile (<= 650px) — the user opens it manually via the legend button.
  useEffect(() => {
    if (awdNoData || awdNoDataProbing) return;
    const prev = prevAppliedRef.current;
    const modelOrVarChanged =
      prev.model !== appliedModel || prev.variable !== appliedVariable;
    prevAppliedRef.current = { model: appliedModel, variable: appliedVariable };
    if (isMobile) return;
    if (!modelOrVarChanged && prev.model !== '') return;
    if (toggleButtonRef.current) {
      setAnchorEl(toggleButtonRef.current);
    }
  }, [awdNoDataProbing, awdNoData, appliedModel, appliedVariable, isMobile]);

  // Close legend when no data is available
  useEffect(() => {
    if (awdNoData) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAnchorEl(null);
    }
  }, [awdNoData]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!anchorEl) {
      e.preventDefault();
    }
  };

  const handleToggle = (e: React.MouseEvent<HTMLElement>) => {
    const legendVisible = isOpen && !hideControls;
    if (legendVisible) {
      setAnchorEl(null);
      toggleButtonRef.current?.focus();
    } else {
      setAnchorEl(e.currentTarget);
      if (isMobile && sidePanelOpen) dispatch(setSidePanelOpen(false));
      toggleButtonRef.current?.focus();
    }
  };

  const returnedFromPopper = useRef<'forward' | 'backward' | null>(null);

  const handleToggleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || isMobile || e.key !== 'Tab') return;

    if (!e.shiftKey) {
      if (returnedFromPopper.current === 'forward') {
        returnedFromPopper.current = null;
        return;
      }
      const focusable = document.querySelectorAll<HTMLElement>('[data-legend-drag-handle]');
      if (focusable?.length) {
        e.preventDefault();
        returnedFromPopper.current = null;
        focusable[0].focus();
      }
    }

    if (e.shiftKey) {
      if (returnedFromPopper.current === 'backward') {
        returnedFromPopper.current = null;
        return;
      }
      const focusable = document.querySelectorAll<HTMLElement>('[data-legend-drag-handle]');
      if (focusable?.length) {
        e.preventDefault();
        returnedFromPopper.current = null;
        focusable[focusable.length - 1].focus();
      }
    }
  };

  return (
    <>
      {isMobile ? (
        <LegendBottomSheet open={isOpen && !hideControls} onClose={() => {
          setAnchorEl(null);
          toggleButtonRef.current?.focus();
        }} />
      ) : (
        isOpen && !hideControls && (
          <Portal container={document.body}>
            <LegendModal anchorEl={anchorEl} onClose={() => {
              setAnchorEl(null);
              toggleButtonRef.current?.focus();
            }} onTabOut={() => {
              returnedFromPopper.current = 'forward';
              toggleButtonRef.current?.focus();
            }} onShiftTabOut={() => {
              returnedFromPopper.current = 'backward';
              toggleButtonRef.current?.focus();
            }} />
          </Portal>
        )
      )}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip
          title="Legend"
          placement="left"
          {...sharedTooltipProps}
        >
          <CustomButton
            ref={toggleButtonRef}
            data-testid="legend-button"
            aria-label="Legend"
            aria-expanded={isOpen}
            disabled={awdNoData}
            buttonVariant="standalone"
            onClick={handleToggle}
            onMouseDown={handleMouseDown}
            onKeyDown={handleToggleKeyDown}
          >
            {<ListAltIcon />}
          </CustomButton>
        </Tooltip>
      </Box>
    </>
  );
});

export default LegendButton;
